<template>
    <div class="control-panel">
        <div class="station sector">
            <div class="station title">{{ $t('control-panel.station') }}</div>
            <hr />
            <div class="station content">
                <select name="weatherStation" v-model="weatherStation" @change="update()">
                    <option v-for="station in StationList" :key="station.id" :value="station.id">
                        {{ $t(`station-name.${station.id}`) }}
                    </option>
                </select>
            </div>
        </div>

        <div class="latest sector">
            <div class="latest title">{{ $t('control-panel.latest') }}</div>
            <hr />
            <div class="latest content">{{ latestDate.yyyy_MM_dd }} {{ latestDate.hh_mm }}</div>
        </div>

        <div class="history sector">
            <div class="history title">{{ $t('control-panel.history') }}</div>
            <hr />
            <div class="history content">
                <input
                    type="date"
                    :max="latestDate.yyyy_MM_dd"
                    v-model="date"
                    @change="update(true)"
                />
                <select name="time" v-model="time" @change="update()">
                    <option v-for="timeSeries in timeSeriesList" :key="timeSeries">
                        {{ timeSeries }}
                    </option>
                </select>
            </div>
        </div>

        <div class="download sector">
            <div class="download title">
                <div :title="$t('control-panel.download-tooltip')">
                    <span>{{ $t('control-panel.download') }}</span>
                    <i class="fa-solid fa-circle-info" style="padding-left: 0.2rem"></i>
                </div>
            </div>
            <hr />
            <div class="download content">
                <DownloadButton />
            </div>
        </div>

        <div class="interval sector">
            <div class="interval title">{{ $t('control-panel.interval') }}</div>
            <hr />
            <div class="interval content">
                <TimeIntervalButton device="desktop" />
            </div>
        </div>

        <div class="language sector">
            <div class="language title">{{ $t('control-panel.language') }}</div>
            <hr />
            <div class="language content">
                <LanguageChangingButton device="desktop" />
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { StationList, resolvePreviewMoment } from '@/constants'
import DownloadButton from '@/components/desktop/DownloadButton.vue'
import TimeIntervalButton from '@/components/common/TimeIntervalButton.vue'
import LanguageChangingButton from '@/components/common/LanguageChangingButton.vue'

export default {
    name: 'ControlPanel',
    components: {
        DownloadButton,
        TimeIntervalButton,
        LanguageChangingButton,
    },
    data() {
        return {
            weatherStation: 'ys',
            date: new Date().toISOString().slice(0, 10),
            time: '00:00',
        }
    },
    computed: {
        ...mapState(['selectedStation', 'selectedDate', 'timeSeriesList', 'latestData']),
        StationList() {
            return StationList
        },
        latestDate() {
            if (!this.latestData || !this.latestData['DateTime'])
                return { yyyy_MM_dd: '--', hh_mm: '--' }
            const dateObj = new Date(this.latestData['DateTime'])
            return {
                yyyy_MM_dd: dateObj
                    .toLocaleDateString([], { year: 'numeric', month: '2-digit', day: '2-digit' })
                    .replaceAll('/', '-'),
                hh_mm: dateObj.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hourCycle: 'h23',
                }),
            }
        },
    },
    methods: {
        ...mapMutations(['setSelectedStation', 'setSelectedDate']),
        ...mapActions([
            'fetchLatestData',
            'fetchHistoryData',
            'fetchTimeSeriesList',
            'fetchGPSPivot',
        ]),
        resetSelectionAndUpdate() {
            // 指定了 previewMoment 的站點停在那一刻，其餘停在最新一筆。
            // 換站與初次載入都會走到這裡，兩邊行為才一致。
            const preview = resolvePreviewMoment(this.weatherStation)
            this.date = preview ? preview.slice(0, 10) : this.latestDate.yyyy_MM_dd
            this.time = preview ? preview.slice(11, 16) : this.latestDate.hh_mm
            this.update()
        },
        async update(setDefaultTime = false) {
            // 各站的資料截止時間不同（白色能源屋目前只有匯入的 7 月資料），
            // 沿用前一站的日期會查到空白頁。換站時先重抓最新一筆，
            // 再由 latestDate 的 watcher 呼叫 resetSelectionAndUpdate 把日期時間
            // 撥到該站最後一筆。只在真的換站時抓，否則會與那個 watcher 互相
            // 觸發成無窮迴圈。
            const stationChanged = this.weatherStation !== this.selectedStation
            this.setSelectedStation(this.weatherStation)
            if (stationChanged) {
                await this.fetchLatestData()
                return
            }
            const selectedDate = new Date(this.date)
            this.setSelectedDate(selectedDate)
            await this.fetchTimeSeriesList()
            if (setDefaultTime) {
                this.time = this.timeSeriesList[0]
            }
            const timeArr = this.time.split(':')
            selectedDate.setHours(timeArr[0])
            selectedDate.setMinutes(timeArr[1])
            await Promise.all([this.fetchHistoryData(), this.fetchGPSPivot()])
        },
    },
    watch: {
        latestDate() {
            this.resetSelectionAndUpdate()
        },
    },
}
</script>

<style lang="scss" scoped>
input,
select,
button {
    font-size: 0.8rem;
    font-family: Avenir, Helvetica, Arial, sans-serif;
    color: black;
    text-align: center;
    border: none;
    border-radius: 4px;
    padding: 4px 0;
}

.control-panel {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    margin: auto 20px;
    max-height: 85%;
    overflow-y: auto;
    overflow-x: auto;
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: flex-start;
    border: #8aaba1 solid 1px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(4px);

    hr {
        width: 96%;
        border: #8aaba1 solid 0.5px;
    }
    .sector {
        width: fit-content;
        height: 100%;
        padding: 0.5rem 1rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        .title {
            font-size: 1rem;
            font-weight: bold;
            text-align: center;
            white-space: nowrap;
        }
        .content {
            border: none;
            border-radius: 4px;
            text-align: center;
            height: 2rem;
            width: fit-content;
            white-space: nowrap;
            display: flex;
            justify-content: center;
            align-items: center;
            &.latest {
                font-size: 0.9rem;
            }
            &.history {
                font-size: 0.8rem;
            }
        }
    }
}

@media (max-width: 1536px) {
    .control-panel {
        margin: auto 10px;
        .sector {
            padding: 0.3rem 0.5rem;
            .title {
                font-size: 0.9rem;
            }
            .content {
                height: 1.8rem;
                &.latest {
                    font-size: 0.8rem;
                }
                &.history {
                    font-size: 0.75rem;
                }
            }
        }
    }
    input,
    select,
    button {
        font-size: 0.75rem;
        padding: 2px 0;
    }
}
@media (max-width: 1280px) {
    .control-panel {
        margin: auto 5px;
        .sector {
            padding: 0.2rem 0.3rem;
            .title {
                font-size: 0.8rem;
            }
            .content {
                height: 1.6rem;
                &.latest {
                    font-size: 0.75rem;
                }
                &.history {
                    font-size: 0.7rem;
                }
            }
        }
    }
    input,
    select,
    button {
        font-size: 0.7rem;
    }
}

::-webkit-scrollbar {
    width: 5px;
}
::-webkit-scrollbar-track {
    background: #dddddd;
    border-radius: 10px;
}
::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 5px;
}
::-webkit-scrollbar-thumb:hover {
    background: #555;
}
::-webkit-scrollbar:horizontal {
    height: 5px;
}
::-webkit-scrollbar-track:horizontal {
    background-color: #dddddd;
}
</style>
